import React, { useEffect, useState } from "react";
import { fetchLeaderboard } from "../api";

const LEVEL_COLORS = {
  Fluent: "bg-emerald-100 text-emerald-800",
  Advanced: "bg-blue-100 text-blue-800",
  Intermediate: "bg-amber-100 text-amber-800",
  Beginner: "bg-slate-100 text-slate-600",
};

const RANK_MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

const LEGACY_COUNTRY_MAP = {
  UK: "GB", "United Kingdom": "GB",
  USA: "US", "United States": "US",
  UAE: "AE", "United Arab Emirates": "AE",
};

const COUNTRY_NAMES = {
  SA: "Saudi Arabia", AE: "UAE", EG: "Egypt", JO: "Jordan", LB: "Lebanon",
  SY: "Syria", IQ: "Iraq", YE: "Yemen", MA: "Morocco", DZ: "Algeria",
  TN: "Tunisia", LY: "Libya", PS: "Palestine", BH: "Bahrain", KW: "Kuwait",
  OM: "Oman", QA: "Qatar", SD: "Sudan", MR: "Mauritania", SO: "Somalia",
  KM: "Comoros", DJ: "Djibouti", GB: "United Kingdom", US: "United States",
  CA: "Canada", AU: "Australia", DE: "Germany", FR: "France", NL: "Netherlands",
  SE: "Sweden", NO: "Norway", DK: "Denmark", PK: "Pakistan", IN: "India",
  TR: "Turkey", ID: "Indonesia", MY: "Malaysia", NG: "Nigeria", ZA: "South Africa",
};

function countryDisplay(value) {
  if (!value) return null;
  const code = (LEGACY_COUNTRY_MAP[value.trim()] || value.trim()).toUpperCase();
  const flag = code.length === 2
    ? code.replace(/./g, (c) => String.fromCodePoint(c.charCodeAt(0) + 127397))
    : value;
  const name = COUNTRY_NAMES[code] || value;
  return { flag, name };
}

function ProgressBar({ value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-[48px] max-w-[80px]">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all"
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-slate-600 shrink-0">{value}%</span>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 ${
        active
          ? "border-emerald-500 text-emerald-700"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function LeaderboardList({ period }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchLeaderboard(period)
      .then((data) => { setRows(data); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [period]);

  const ownRow = rows.findIndex((r) => r.is_current_user) >= 50
    ? rows.find((r) => r.is_current_user)
    : undefined;

  const emptyMsg = period === "monthly"
    ? "No topics completed yet this month. Be the first!"
    : "No activity yet. Complete some topics to appear here!";

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>;
  }
  if (error) {
    return <div className="flex items-center justify-center py-16 text-red-500 text-sm">{error}</div>;
  }
  if (rows.length === 0) {
    return <div className="flex items-center justify-center py-16 text-slate-400 text-sm px-4 text-center">{emptyMsg}</div>;
  }

  return (
    <div className="p-3 space-y-2">
      {rows.map((row) => (
        <LeaderboardRow key={row.user_id} row={row} isMe={row.is_current_user} period={period} />
      ))}

      {ownRow && (
        <>
          <div className="flex items-center gap-2 py-1">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 shrink-0">Your position</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <LeaderboardRow row={ownRow} isMe period={period} />
        </>
      )}
    </div>
  );
}

export default function Leaderboard({ onClose }) {
  const [tab, setTab] = useState("alltime");

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-lg">🏆</span>
          <h2 className="text-base font-bold text-slate-900">Leaderboard</h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
            aria-label="Close leaderboard"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 shrink-0 px-2">
        <TabButton active={tab === "alltime"} onClick={() => setTab("alltime")}>
          All Time
        </TabButton>
        <TabButton active={tab === "monthly"} onClick={() => setTab("monthly")}>
          This Month
        </TabButton>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <LeaderboardList key={tab} period={tab} />
      </div>
    </div>
  );
}

function LeaderboardRow({ row, isMe, period }) {
  const country = countryDisplay(row.country);
  return (
    <div className={`surface-glass surface--interactive rounded-xl px-4 py-3 ${isMe ? "surface--active" : ""}`}>
      {/* Top line: rank + name + level badge */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base w-7 text-center shrink-0">
            {RANK_MEDAL[row.rank] || <span className="text-xs font-semibold text-slate-500">#{row.rank}</span>}
          </span>
          <span className="font-semibold text-slate-800 text-sm truncate">{row.student}</span>
          {isMe && (
            <span className="shrink-0 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
              You
            </span>
          )}
        </div>
        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${LEVEL_COLORS[row.level] || LEVEL_COLORS.Beginner}`}>
          {row.level}
        </span>
      </div>

      {/* Bottom line: stat + country */}
      <div className="flex items-center gap-3 pl-9">
        {period === "monthly" ? (
          <span className="text-sm font-semibold text-slate-700 tabular-nums">
            {row.topics_completed}
            <span className="text-xs font-normal text-slate-500 ml-1">
              {row.topics_completed === 1 ? "topic" : "topics"} this month
            </span>
          </span>
        ) : (
          <>
            <ProgressBar value={row.overall_progress} />
            <span className="text-xs text-slate-500 shrink-0">
              <span className="font-semibold text-slate-700 tabular-nums">{row.topics_completed}</span> topics
            </span>
          </>
        )}
        {country && (
          <span className="text-xs text-slate-500 shrink-0 flex items-center gap-1 ml-auto">
            <span>{country.flag}</span>
            <span className="hidden sm:inline">{country.name}</span>
          </span>
        )}
      </div>
    </div>
  );
}

